import React from 'react';

import { css } from '@emotion/react';
import { Box, Text } from '@opendoor/bricks-next';
import { Image } from '@opendoor/bricks/core';

import { ArticleHeader } from 'components/articles-v2/ArticleHeader';
import { ArticleContainer, ArticleHeaderContainer } from 'components/articles-v2/constants';
import SeoHead from 'components/articles-v2/seo';

import LandingPageV2Layout from 'pages/shared/LandingPageV2Layout';

import { ArticleProps, fetchArticleBySlug } from '../../cms';
import articleV2EntryLookup from '../../cms/entries/articleV2';
import RichTextRenderer from '../../components/articles-v2/renderer';
import {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from '../../declarations/shared/serverSideProps';

const MAX_WIDTH = 1440;
const HEADER_HEIGHT = 65;

const Article: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  return (
    <LandingPageV2Layout {...props}>
      <SeoHead
        slug={props.fields.slug}
        articleName={props.fields.articleName}
        articleSubtitle={props.fields.articleSubtitle}
        headerSummary={props.fields.headerSummary}
        og={props.fields.openGraph}
        ogArticle={props.fields.openGraphArticle}
        base={props.fields.seoBase}
        authors={props.fields.authors}
        editors={props.fields.editors}
        reviewers={props.fields.reviewers}
        twitterCard={props.fields.twitterCard}
        sys={props.sys}
        publisher={props.fields.publisher}
        publishDate={props.fields.publishDate}
      />
      <Box tag="article" backgroundColor="$backgroundSecondary" marginTop={HEADER_HEIGHT}>
        <Box
          backgroundColor="$backgroundPrimary"
          paddingVertical={64}
          paddingHorizontal={24}
          borderBottomRightRadius={64}
          borderBottomLeftRadius={64}
        >
          <Box
            maxWidth={MAX_WIDTH}
            marginHorizontal="auto"
            width="100%"
            $largerThanSM={{ paddingHorizontal: '$10x' }}
            $largerThanMD={{ paddingHorizontal: '$20x' }}
            $largerThanLG={{ paddingHorizontal: '$32x' }}
          >
            <ArticleHeader {...props} />

            {props.fields.keyTakeaways && props.fields.keyTakeaways?.content.length > 0 && (
              <Box>
                <Box $largerThanSM={{ marginLeft: 'calc((4 * 1/12) * 100%)' }}>
                  <Text tag="p" typography="$bodyMedium" marginBottom="$8x">
                    Summary
                  </Text>
                  <ArticleHeaderContainer className="font_body">
                    <RichTextRenderer.Component
                      body={props.fields.keyTakeaways}
                      idToLoaderData={props.idToLoaderData}
                      entryLookup={articleV2EntryLookup}
                    />
                  </ArticleHeaderContainer>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <ArticleContainer
          style={{
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          }}
        >
          <RichTextRenderer.Component
            body={props.fields.body}
            idToLoaderData={props.idToLoaderData}
            entryLookup={articleV2EntryLookup}
          />
        </ArticleContainer>
        {props.fields.showAuthorFooter && (
          <Box justifyContent="center" flexDirection="row" marginBottom={64} paddingHorizontal={24}>
            <Box
              $largerThanSM={{ flexDirection: 'row' }}
              alignItems="center"
              columnGap="$8x"
              rowGap="$16x"
            >
              {props.fields.authors?.[0].fields.picture && (
                <Box
                  height={96}
                  width={96}
                  css={css`
                    flex-shrink: 0;
                  `}
                >
                  <Image
                    src={props.fields.authors?.[0].fields.picture}
                    alt={`Photo of ${props.fields.authors?.[0].fields.displayName}`}
                    css={css`
                      object-fit: cover;
                      width: 100%;
                      height: 100%;
                      border-radius: 50%;
                    `}
                  />
                </Box>
              )}
              <Box>
                <Text
                  tag="p"
                  typography="$subheaderXsmall"
                  $largerThanSM={{ typography: '$subheaderSmall' }}
                  marginBottom="$4x"
                >
                  {props.fields.authors?.[0].fields.displayName &&
                    props.fields.authors?.[0].fields.displayName}
                </Text>
                <Text
                  tag="p"
                  typography="$subheaderXsmall"
                  $largerThanSM={{ typography: '$subheaderSmall' }}
                >
                  {props.fields.authors?.[0].fields.bio && props.fields.authors?.[0].fields.bio}
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </LandingPageV2Layout>
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<
  ArticleProps,
  { name: Array<string> }
> = async (context) => {
  if (!context.params) {
    return {
      notFound: true,
    };
  }
  try {
    const props = await fetchArticleBySlug({
      slug: `/articles/${context.params.name.join('/')}`,
      requestUrl: context.resolvedUrl,
    });
    if (!props) {
      return {
        notFound: true,
      };
    }
    return {
      props,
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export default Article;
